<template>
  <div>
    <div
      style="display: flex;"
    >
      <!--      label-->
      <div
        v-if="fieldLabel"
        id="label"
        :title="fieldLabel"
      >
        {{ fieldLabel }}
      </div>
      <!--      tooltip-->
      <v-tooltip
        v-if="hasTooltip"
        top
        :color="tooltipColor"
      >
        <template
          #activator="{ on, attrs }"
        >
          <v-icon
            class="ml-2"
            :style="tooltipIconStyle"
            v-bind="attrs"
            v-on="on"
          >
            {{ tooltipIcon }}
          </v-icon>
        </template>
        <!--        Tooltip text-->
        <template
          v-if="tooltipText"
        >
          <span>
            {{ tooltipText }}
          </span>
        </template>

        <!--        Tooltip custom component-->
        <template
          v-else-if="tooltipComponent"
        >
          <component
            :is="tooltipComponent"
            :config="tooltipConfig"
          />
        </template>
      </v-tooltip>
    </div>
    <v-combobox
      v-model="selectedItems"
      :error-messages="errorMessages"
      :error-count="errorMessages.length"
      :placeholder="placeholder"
      :hide-details="hideDetails"
      :items="processedItems"
      :item-value="itemValueKey"
      item-text="displayText"
      :autocomplete="fieldAutocomplete"
      :class="fieldClasses"
      :style="fieldStyle"
      :autofocus="autofocus"
      :label="label"
      :append-icon="appendIcon"
      :menu-props="{
        contentClass: 'combobox-menu'
      }"
      :clearable="isClearable"
      :hide-selected="hideSelected"
      :multiple="isMultiple"
      :outlined="outlined"
      :search-input.sync="searchInput"
      :dense="dense"
      :solo="solo"
      :flat="flat"
      :readonly="readonly"
      :disabled="disabled"
      :loading="isSearching"
      @change="onChange"
      @focusin="onFocusIn"
      @focusout="onFocusOut"
      @keydown.enter.stop
    >
      <!-- .stop above: Enter selects/confirms a combobox item and shouldn't
           bubble up to a parent dialog's own Enter handler (eg. TableFiltersDialog's
           "apply filters on Enter") and close it instead of just picking the item -->
      <template #selection="{ attrs, item, parent, selectedValue }">
        <!--        show selected items as a chips-->
        <template
          v-if="showItemsAsChipsComputed && item === Object(item)"
        >
          <v-chip
            v-bind="attrs"
            :input-value="selectedValue"
            label
            small
            class="chip"
            :color="chipColor"
          >
            <span
              class="pr-2 chip-text"
              :title="getItemText(item)"
            >
              {{ getItemText(item) }}
            </span>
            <v-icon
              v-if="!disabled && !readonly"
              small
              @click="parent.selectItem(item)"
            >
              $delete
            </v-icon>
          </v-chip>
        </template>
        <template
          v-else
        >
          <span
            class="pr-2 selected-item"
          >
            {{ getItemText(item) }}
          </span>
        </template>
      </template>
      <template
        #append-item
      >
        <v-list-item
          v-if="hasMoreItems"
          class="has-more-items"
          @click="makeSearch('', true)"
        >
          <v-list-item-title
            class="font-italic text-decoration-underline has-more-title"
          >
            {{ translate('base.got_more_items') }}
          </v-list-item-title>
        </v-list-item>
      </template>
    </v-combobox>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { isArray, isEmpty, isNumeric, isObject, isString, isFunction } from '@/global/utils/utils'
import { api } from '@/global/services/api'
import { useTranslate } from '@/global/composables/useTranslate'

const DEFAULT_ITEM_VALUE = 'id'
const DEFAULT_ITEM_TEXT = 'name'
const DEFAULT_TOOLTIP_COLOR = '#6B7280'
const DEFAULT_TOOLTIP_ICON = 'mdi-information-slab-circle'
const DEFAULT_TOOLTIP_TEXT = ''
const DEFAULT_TOOLTIP_FONT_SIZE = '16px'
const DEFAULT_TOOLTIP_MARGIN_BOTTOM = '1px'
const DEFAULT_PLACEHOLDER_VALUE = ''
const DEFAULT_SEARCH_TIMEOUT = 500
// consistent with AutocompleteField: search is on by default, and can only be
// turned off explicitly via `options.allowSearch === false`
const DEFAULT_ALLOW_SEARCH = false
const DEFAULT_AUTOFOCUS = false
const DEFAULT_CHIP_COLOR = '#E5E7EB'

const { translate } = useTranslate()

const props = defineProps({
  readonly: {
    type: Boolean,
    default: false
  },
  outlined: {
    type: Boolean,
    default: true
  },
  dense: {
    type: Boolean,
    default: true
  },
  field: {
    type: Object,
    default: () => ({})
  },
  solo: {
    type: Boolean,
    default: false
  },
  flat: {
    type: Boolean,
    default: true
  },
  value: {
    type: [Object, Array, Number],
    default: null
  },
  options: {
    type: Object,
    default: () => ({})
  },
  validationErrors: {
    type: Object,
    default: () => ({})
  },
  label: {
    type: String,
    default: ''
  },
  hideDetails: {
    type: [Boolean, String],
    default: 'auto'
  },
  appendIcon: {
    type: String,
    default: '$dropdown'
  },
  items: {
    type: Object,
    default: () => ({})
  },
  disabled: {
    type: Boolean,
    default: false
  },
  showItemsAsChips: {
    type: Boolean,
    default: true
  },
  // Fields are clearable unless a config/caller says otherwise.
  clearable: {
    type: Boolean,
    default: true
  },
  hideSelected: {
    type: Boolean,
    default: true
  },
  multiple: {
    type: Boolean,
    default: true
  },
  customFieldKey: {
    type: String,
    default: ''
  },
  editMode: {
    type: Boolean,
    default: false
  },
  createMode: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits([
  'clear-validation-errors',
  'input',
  'items',
  'focusin',
  'focusout'
])

const internalItems = ref([])
const hasMoreItems = ref(false)
const searchInput = ref('')
const isSearching = ref(false)
let searchTimeoutId = null

const isMultiple = computed(() => {
  const { multiple } = props.field || {}
  return multiple !== undefined ? !!multiple : props.multiple
})

const showItemsAsChipsComputed = computed(() => {
  const { showItemsAsChips } = props.field || {}
  return showItemsAsChips !== undefined ? !!showItemsAsChips : props.showItemsAsChips
})

const itemValueKey = computed(() => {
  const { form_value: formValue } = props.options || {}
  return formValue && isString(formValue) && !formValue.includes('.') ? formValue : DEFAULT_ITEM_VALUE
})

const autofocus = computed(() => {
  const { autofocus: fieldAutofocus } = props.field || {}
  return fieldAutofocus !== undefined ? !!fieldAutofocus : DEFAULT_AUTOFOCUS
})

// Optional native autocomplete attribute for the underlying input (e.g. 'off'
// to suppress the browser's suggestion dropdown). Undefined => attribute omitted,
// so existing usages are unaffected.
const fieldAutocomplete = computed(() => props.field?.autocomplete)

const placeholder = computed(() => {
  const { placeholder: fieldPlaceholder } = props.field || {}
  return fieldPlaceholder && isString(fieldPlaceholder) && (props.editMode || props.createMode)
    ? fieldPlaceholder
    : DEFAULT_PLACEHOLDER_VALUE
})

const chipColor = computed(() => DEFAULT_CHIP_COLOR)

// `??`, not `||`: the default is `true`, so an explicit `clearable: false` in the
// field config has to be able to win. Vuetify draws the clear icon on readonly
// inputs too (and it stays clickable there), so the field also has to actually be
// editable for it to make sense.
const isClearable = computed(() =>
  (props.field?.clearable ?? props.clearable) && !props.readonly && !props.disabled
)

// the custom label rendered above the field comes from the field config,
// distinct from the `label` prop forwarded to v-combobox's own floating label
const fieldLabel = computed(() => {
  const { label: fieldLabelText } = props.field || {}
  return isString(fieldLabelText) && fieldLabelText ? fieldLabelText : ''
})

// Non-editable fields get no grey background (see TextField): that state is
// carried by the text and cursor, not by the box - so the only styling left here
// is whatever the field config asks for.
const fieldStyle = computed(() => {
  const { style: fieldConfigStyle } = props.field || {}

  return fieldConfigStyle && isObject(fieldConfigStyle) ? { ...fieldConfigStyle } : {}
})

const errorMessages = computed(() => {
  const { key: fieldKey, validationErrorIncludeKey, validationPropertyKey } = props.field || {}

  if (isEmpty(props.validationErrors) || !fieldKey || props.disabled || props.readonly) {
    return []
  }

  let messages = []
  for (const errorKey in props.validationErrors) {
    if (errorKey === fieldKey) {
      messages = props.validationErrors[errorKey]
    }
    else if (validationPropertyKey && errorKey === validationPropertyKey) {
      messages = props.validationErrors[errorKey]
    }
    else if (errorKey === props.customFieldKey) {
      messages = props.validationErrors[errorKey]
    }
    else if (validationErrorIncludeKey && errorKey?.includes(validationErrorIncludeKey)) {
      messages = props.validationErrors[errorKey]
    }
  }

  return messages
})

const fieldClasses = computed(() => {
  const { class: fieldClass, useDefaultEditStyle = true } = props.field || {}
  let classes = 'mt-1'

  if (props.editMode && !props.disabled && useDefaultEditStyle) {
    classes += ' combobox-field--is-editing'
  }
  if (props.readonly) {
    classes += ' combobox-field--readonly'
  }
  if (errorMessages && errorMessages?.length) {
    classes += ' combobox-field--has-error'
  }
  if (fieldClass && isString(fieldClass)) {
    classes += ' ' + fieldClass
  }

  return classes
})

const hasTooltip = computed(() => {
  const { tooltip } = props.field || {}
  return !!(tooltip && isObject(tooltip))
})

const tooltipColor = computed(() => props.field?.tooltip?.color || undefined)
const tooltipText = computed(() => props.field?.tooltip?.text || DEFAULT_TOOLTIP_TEXT)
const tooltipIcon = computed(() => props.field?.tooltip?.icon || DEFAULT_TOOLTIP_ICON)
const tooltipComponent = computed(() => props.field?.tooltip?.component)
const tooltipConfig = computed(() => props.field?.tooltip?.config || {})
const tooltipIconStyle = computed(() => props.field?.tooltip?.style || {
  color: DEFAULT_TOOLTIP_COLOR,
  fontSize: DEFAULT_TOOLTIP_FONT_SIZE,
  marginBottom: DEFAULT_TOOLTIP_MARGIN_BOTTOM
})

function getItemText (item) {
  const { custom_display_value: customDisplayValue } = props.options || {}

  if (isFunction(customDisplayValue)) {
    const customText = customDisplayValue(item)
    if (customText && isString(customText)) {
      return customText
    }
  }

  if (isString(item)) {
    return item
  }

  const { name } = item || {}
  return isString(name) ? name : ''
}

function normalizeId (item) {
  const { id } = item

  if (!isString(id)) {
    return { ...item, id }
  }

  // keep string ids that contain letters as-is, otherwise treat them as numeric ids
  if (/[a-zA-Z]/.test(id)) {
    return { ...item, id }
  }

  const parsedId = parseInt(id, 10)
  return { ...item, id: isNaN(parsedId) ? undefined : parsedId }
}

const processedItems = computed(() => internalItems.value
  .map(item => ({ ...item, displayText: getItemText(item) }))
  .filter(item => item.displayText))

// let consumers react to resolved items without coupling that emission to
// the (pure) processedItems getter
watch(processedItems, (items) => {
  emit('items', { items })
}, { immediate: true })

function findSearchInputInItems (val) {
  return processedItems.value.find(item =>
    (getItemText(item) && getItemText(item) === val) ||
    item?.[itemValueKey.value] === val ||
    item?.[DEFAULT_ITEM_TEXT] === val)
}

// a combobox lets the user confirm typed text that matched no known item -
// Vuetify then puts that raw string straight into the model (single value or,
// in multiple mode, as one of the array's elements) instead of an
// `{ id, name }` object like every other entry. If the text actually matches
// a known item, resolve it to that item; otherwise either keep it as a
// free-text entry (default, eg. EmailSelection's "choose or enter an email")
// or drop it when the field opts out via `options.allowCustomValue: false`
// (eg. a combobox that filters by an existing entity's id, where arbitrary
// typed text can never be a valid value).
function normalizeFreeTextEntry (entry) {
  if (!entry || isObject(entry)) {
    return entry
  }

  const matchedItem = findSearchInputInItems(entry)
  if (matchedItem) {
    return matchedItem
  }

  const { allowCustomValue = true } = props.options || {}
  return allowCustomValue ? { id: entry, name: entry } : undefined
}

const selectedItems = computed({
  get: () => props.value,
  set: (newVal) => {
    const val = isArray(newVal)
      ? newVal.map(normalizeFreeTextEntry).filter(entry => entry !== undefined)
      : normalizeFreeTextEntry(newVal)

    emit('input', val === undefined ? null : val)
  }
})

async function makeSearch (query = '', allItems = false) {
  try {
    const { module, route, query: optionsQuery } = props.options || {}

    if (!module && !route) {
      return
    }

    let params = {
      query,
      pickedId: null,
      includeAll: allItems
    }

    if (isFunction(optionsQuery)) {
      params = { ...params, ...optionsQuery({ allItems }) }
    }
    else if (optionsQuery && isObject(optionsQuery)) {
      params = { ...params, ...optionsQuery }
    }

    isSearching.value = true

    const { data, has_more: hasMore = false } = await api(module).get(route, params)
    let normalizedData = data

    // case when data is object (NOT array) with numeric keys
    // then convert it into array of objects
    if (isObject(data) && !isArray(data)) {
      const isNumericKeys = Object.keys(data).every(key => isNumeric(key))
      if (isNumericKeys) {
        normalizedData = Object.values(data)
      }
    }

    if (normalizedData?.length) {
      internalItems.value = normalizedData.map(normalizeId)
      hasMoreItems.value = hasMore
    }
  }
  catch (exception) {
    console.error(exception)
  }
  finally {
    isSearching.value = false
  }
}

watch(searchInput, (val) => {
  const { allowSearch, onSearchInput } = props.options || {}

  if (val === null || val === undefined || !isString(val) || !(allowSearch ?? DEFAULT_ALLOW_SEARCH)) {
    return
  }

  if (!val) {
    searchInput.value = ''
    selectedItems.value = null
  }

  clearTimeout(searchTimeoutId)
  searchTimeoutId = setTimeout(async () => {
    if (!findSearchInputInItems(val)) {
      await makeSearch(val)
    }
    if (isFunction(onSearchInput)) {
      onSearchInput({ val })
    }
  }, DEFAULT_SEARCH_TIMEOUT)
})

watch(() => props.items, (items) => {
  const { key: fieldKey } = props.field || {}
  const { data, hasMore } = items?.[fieldKey] || items || {}

  if (data?.length) {
    internalItems.value = data.map(normalizeId)
    hasMoreItems.value = !!hasMore
  }
}, { immediate: true, deep: true })

onBeforeUnmount(() => {
  clearTimeout(searchTimeoutId)
})

const { fetchInitial } = props.options || {}
if (fetchInitial === true || fetchInitial === undefined) {
  makeSearch()
}

function onChange () {
  emit('clear-validation-errors')
}

function onFocusIn () {
  const { onFocusIn: fieldOnFocusIn } = props.field || {}
  if (isFunction(fieldOnFocusIn)) {
    fieldOnFocusIn()
  }
  emit('focusin')
}

function onFocusOut () {
  const { onFocusOut: fieldOnFocusOut } = props.field || {}
  if (isFunction(fieldOnFocusOut)) {
    fieldOnFocusOut()
  }
  emit('focusout')
}
</script>

<style scoped lang="scss">
.has-more-items {
  cursor: pointer !important;
}

.has-more-title {
  font-family: 'Satoshi', sans-serif;
  font-size: 14px !important;
  line-height: 20px !important;
  font-weight: 900 !important;
}

::v-deep.v-input--is-disabled > .v-input__control > .v-input__slot > .v-select__slot > input {
  font-family: 'Satoshi', sans-serif;
  font-size: 14px !important;
  line-height: 20px !important;
  color: #9CA3AF !important;
  font-weight: 400 !important;
}

::v-deep {
  // disabled style
  .v-input--is-disabled > .v-input__control > .v-input__slot > .v-select__slot > input {
    font-family: 'Satoshi', sans-serif;
    font-size: 14px !important;
    line-height: 20px !important;
    color: #9CA3AF !important;
    font-weight: 400 !important;
  }

  .v-text-field--outlined fieldset {
    border-color: #E5E7EB !important;
    border-radius: 8px !important;
  }
  .combobox-field--is-editing fieldset {
    border-color: #65AD45 !important;
  }
  .v-input > .v-input__control > .v-input__slot > .v-select__slot > .v-select__selections input {
    font-family: 'Satoshi', sans-serif;
    font-size: 14px !important;
    line-height: 20px !important;
    color: #111827;
    font-weight: 500;
  }

  // readonly style
  .combobox-field--readonly > .v-input__control > .v-input__slot > .v-select__slot > input {
    font-family: 'Satoshi', sans-serif;
    font-size: 14px !important;
    line-height: 20px !important;
    color: #9CA3AF !important;
    font-weight: 400 !important;
  }

  // Scoped to the input box itself (`.v-input__slot`), not the whole
  // `.v-input` - that also contains `.v-text-field__details` (the
  // validation message row below), which would otherwise get painted
  // green too and look like the field stretched down over the error text.
  .combobox-field--is-editing > .v-input__control > .v-input__slot {
    background-color: #F7FEE7 !important;
  }

  // Placed after the default/editing border rules above so it wins the
  // (equal-specificity) cascade regardless of edit state.
  .combobox-field--has-error fieldset {
    border-color: #DC2626 !important;
  }
}

#label {
  font-family: 'Satoshi', sans-serif;
  font-size: 14px;
  color: #111827;
  line-height: 20px;
  font-weight: 500;
  /* Never wrap - see TextField. `min-width: 0` is needed because this label is a
     flex item next to the tooltip icon. */
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.selected-item {
  font-family: 'Satoshi', sans-serif;
  font-size: 14px;
  color: #111827;
  line-height: 20px;
  font-weight: 500;
}

.chip {
  margin: 4px !important;
  max-width: 100%;
}

.chip-text {
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: inline-block;
  vertical-align: middle;
}
</style>
